// src/components/Chat/ChatWindow.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  Button,
  Chip,
  Badge,
  Tooltip,
  LinearProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Send,
  AttachFile,
  WhatsApp,
  Download,
  Close,
  Delete,
  EmojiEmotions,
  SmartToy,
  Mic,
  Videocam,
  MoreVert,
  Check,
  CheckCircle,
  KeyboardVoice,
  Stop,
  InsertPhoto,
  PictureAsPdf,
  Description,
  Cancel,
} from '@mui/icons-material';
import { formatDateTime } from '../../utils/helpers';
import { chatAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import io from 'socket.io-client';
import { motion, AnimatePresence } from 'framer-motion';

const ChatWindow = ({ enquiryId, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioRecorder, setAudioRecorder] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const { user } = useAuth();

  // Format time ago helper function
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'Just now';
    
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    
    return formatDateTime(dateString);
  };

  useEffect(() => {
    // Initialize socket connection
    const newSocket = io('http://localhost:5000', {
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });
    
    setSocket(newSocket);

    newSocket.on('connect', () => {
      setIsConnected(true);
      showNotification('Connected to chat');
    });

    newSocket.on('disconnect', () => {
      setIsConnected(false);
      showNotification('Disconnected from chat');
    });

    newSocket.emit('join-enquiry', enquiryId);
    
    newSocket.on('new-message', (message) => {
      setMessages(prev => [...prev, message]);
      scrollToBottom();
    });

    newSocket.on('user-typing', ({ userId, isTyping }) => {
      if (userId !== user?.id) {
        setIsTyping(isTyping);
      }
    });

    loadChatHistory();

    return () => {
      newSocket.disconnect();
    };
  }, [enquiryId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    let interval;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRecording]);

  const loadChatHistory = async () => {
    try {
      setLoading(true);
      const response = await chatAPI.getMessages(enquiryId);
      setMessages(response.data);
      scrollToBottom();
    } catch (error) {
      console.error('Error loading chat:', error);
      showNotification('Failed to load chat history');
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && !file) return;

    const messageData = {
      message: newMessage,
      file: file,
      enquiryId: enquiryId,
      sender_id: user?.id,
      sender_name: user?.name || 'Anonymous',
    };

    try {
      setLoading(true);
      
      // Emit typing stopped
      if (socket) {
        socket.emit('typing', { userId: user?.id, isTyping: false });
      }

      // Upload with progress
      const formData = new FormData();
      formData.append('message', newMessage);
      formData.append('enquiryId', enquiryId);
      if (file) {
        formData.append('file', file);
      }

      const response = await chatAPI.sendMessage(formData, {
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(progress);
        },
      });

      // Clear inputs
      setNewMessage('');
      setFile(null);
      setUploadProgress(0);
      setShowEmojiPicker(false);

      // Add message to local state
      setMessages(prev => [...prev, response.data]);

      // Notify success
      showNotification('Message sent successfully');

    } catch (error) {
      console.error('Error sending message:', error);
      showNotification('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const handleTyping = () => {
    if (socket && user) {
      socket.emit('typing', { userId: user.id, isTyping: true });
      // Stop typing after 2 seconds
      setTimeout(() => {
        socket.emit('typing', { userId: user.id, isTyping: false });
      }, 2000);
    }
  };

  const handleFileUpload = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      // Validate file size (max 10MB)
      if (selectedFile.size > 10 * 1024 * 1024) {
        showNotification('File size must be less than 10MB');
        return;
      }
      
      // Validate file type
      const allowedTypes = ['image/', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.some(type => selectedFile.type.startsWith(type))) {
        showNotification('Only images, PDFs, and Word documents are allowed');
        return;
      }
      
      setFile(selectedFile);
      showNotification('File selected: ' + selectedFile.name);
    }
  };

  const removeFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleWhatsAppRedirect = () => {
    const message = `Hello! I have a question about enquiry ${enquiryId}.`;
    const whatsappUrl = `https://wa.me/1234567890?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleEmojiClick = (emoji) => {
    // Simple emoji picker without external dependency
    const emojis = {
      smile: '😊',
      heart: '❤️',
      thumbsup: '👍',
      fire: '🔥',
      check: '✅',
      warning: '⚠️',
      question: '❓',
      star: '⭐',
      party: '🎉',
      clap: '👏'
    };
    
    setNewMessage(prev => prev + (emojis[emoji] || '😊'));
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const audioChunks = [];

      recorder.ondataavailable = (event) => {
        audioChunks.push(event.data);
      };

      recorder.onstop = () => {
        const audioBlob = new Blob(audioChunks, { type: 'audio/wav' });
        const audioFile = new File([audioBlob], `audio_${Date.now()}.wav`, { type: 'audio/wav' });
        setFile(audioFile);
        showNotification('Audio recorded successfully');
      };

      recorder.start();
      setAudioRecorder(recorder);
      setIsRecording(true);
      setRecordingTime(0);
    } catch (error) {
      console.error('Error starting recording:', error);
      showNotification('Failed to start recording');
    }
  };

  const stopRecording = () => {
    if (audioRecorder) {
      audioRecorder.stop();
      setIsRecording(false);
      setRecordingTime(0);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        await chatAPI.deleteMessage(messageId);
        setMessages(prev => prev.filter(msg => msg.id !== messageId));
        showNotification('Message deleted');
      } catch (error) {
        console.error('Error deleting message:', error);
        showNotification('Failed to delete message');
      }
    }
  };

  const showNotification = (message) => {
    setSnackbarMessage(message);
    setShowSnackbar(true);
  };

  const isOwnMessage = (senderId) => {
    return senderId === user?.id;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
      return <InsertPhoto color="primary" />;
    } else if (extension === 'pdf') {
      return <PictureAsPdf color="error" />;
    } else if (['doc', 'docx'].includes(extension)) {
      return <Description color="info" />;
    }
    return <AttachFile />;
  };

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Box sx={{ height: '600px', display: 'flex', flexDirection: 'column' }}>
      {/* Connection Status */}
      <Box sx={{ 
        p: 1, 
        bgcolor: isConnected ? '#4caf50' : '#f44336',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        fontSize: '0.875rem',
      }}>
        <Box 
          sx={{ 
            width: 8, 
            height: 8, 
            borderRadius: '50%', 
            bgcolor: isConnected ? '#ffffff' : 'transparent',
            animation: isConnected ? 'pulse 2s infinite' : 'none',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.5 },
            }
          }} 
        />
        {isConnected ? 'Connected' : 'Disconnected'}
      </Box>

      {/* Chat Header */}
      <Paper sx={{ 
        p: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ 
            bgcolor: 'primary.main',
            width: 40,
            height: 40,
          }}>
            <SmartToy />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Live Support Chat
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Enquiry #{enquiryId} • 24/7 Support
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip 
            label="AI Assistant" 
            size="small" 
            color="primary" 
            variant="outlined"
            icon={<SmartToy />}
          />
          <Tooltip title="Continue on WhatsApp">
            <IconButton 
              onClick={handleWhatsAppRedirect}
              color="success"
              size="small"
            >
              <WhatsApp />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close chat">
            <IconButton 
              onClick={onClose}
              size="small"
            >
              <Close />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Messages Container */}
      <Paper sx={{ 
        flex: 1, 
        overflow: 'auto', 
        p: 2, 
        bgcolor: 'grey.50',
        position: 'relative',
      }}>
        {loading && messages.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <List>
            <AnimatePresence>
              {messages.map((msg, index) => (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <ListItem
                    sx={{
                      flexDirection: isOwnMessage(msg.sender_id) ? 'row-reverse' : 'row',
                      alignItems: 'flex-start',
                      mb: 2,
                      px: 1,
                    }}
                  >
                    <ListItemAvatar>
                      <motion.div
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <Avatar 
                          sx={{ 
                            bgcolor: isOwnMessage(msg.sender_id) ? 'primary.main' : 'secondary.main',
                            width: 36,
                            height: 36,
                            fontSize: '0.875rem',
                          }}
                        >
                          {isOwnMessage(msg.sender_id) ? 'Me' : 'AI'}
                        </Avatar>
                      </motion.div>
                    </ListItemAvatar>
                    
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            bgcolor: isOwnMessage(msg.sender_id) ? 'primary.light' : 'white',
                            color: isOwnMessage(msg.sender_id) ? 'primary.contrastText' : 'text.primary',
                            p: 2,
                            borderRadius: 2,
                            maxWidth: '70%',
                            border: '1px solid',
                            borderColor: isOwnMessage(msg.sender_id) ? 'primary.main' : 'grey.300',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            position: 'relative',
                          }}
                        >
                          {/* Message Header */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="caption" fontWeight="bold">
                              {isOwnMessage(msg.sender_id) ? 'You' : 'AI Assistant'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                {formatTimeAgo(msg.created_at)}
                              </Typography>
                              {isOwnMessage(msg.sender_id) && (
                                <Tooltip title="Delete message">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleDeleteMessage(msg.id)}
                                    sx={{ p: 0.25 }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </Box>

                          {/* Message Content */}
                          {msg.message_type === 'file' ? (
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 2,
                              p: 1.5,
                              borderRadius: 1,
                              bgcolor: 'rgba(0,0,0,0.05)',
                            }}>
                              {getFileIcon(msg.file_name)}
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" fontWeight="medium">
                                  {msg.file_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatFileSize(msg.file_size)}
                                </Typography>
                              </Box>
                              <Button
                                size="small"
                                startIcon={<Download />}
                                href={`http://localhost:5000${msg.file_url}`}
                                target="_blank"
                                variant="contained"
                                sx={{ minWidth: 'auto', p: '4px 8px' }}
                              >
                                Download
                              </Button>
                            </Box>
                          ) : (
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                              {msg.message}
                            </Typography>
                          )}

                          {/* Message Footer */}
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'flex-end', 
                            alignItems: 'center', 
                            gap: 0.5, 
                            mt: 1,
                            opacity: 0.7,
                          }}>
                            <Check fontSize="small" />
                            <Typography variant="caption">
                              Delivered
                            </Typography>
                          </Box>
                        </Box>
                      }
                      sx={{ 
                        ml: isOwnMessage(msg.sender_id) ? 0 : 1, 
                        mr: isOwnMessage(msg.sender_id) ? 1 : 0,
                      }}
                    />
                  </ListItem>
                </motion.div>
              ))}
            </AnimatePresence>
            
            {/* Typing Indicator */}
            {isTyping && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
              >
                <ListItem sx={{ justifyContent: 'flex-start' }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'secondary.main', width: 36, height: 36 }}>
                      <SmartToy />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          bgcolor: 'white',
                          p: 2,
                          borderRadius: 2,
                          maxWidth: '40%',
                          border: '1px solid',
                          borderColor: 'grey.300',
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          <Box sx={{ 
                            width: 8, 
                            height: 8, 
                            borderRadius: '50%', 
                            bgcolor: 'grey.400',
                            animation: 'typing 1.4s infinite',
                          }} />
                          <Box sx={{ 
                            width: 8, 
                            height: 8, 
                            borderRadius: '50%', 
                            bgcolor: 'grey.400',
                            animation: 'typing 1.4s infinite 0.2s',
                          }} />
                          <Box sx={{ 
                            width: 8, 
                            height: 8, 
                            borderRadius: '50%', 
                            bgcolor: 'grey.400',
                            animation: 'typing 1.4s infinite 0.4s',
                          }} />
                          <Typography variant="caption" sx={{ ml: 1 }}>
                            AI Assistant is typing...
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
              </motion.div>
            )}
            
            <div ref={messagesEndRef} />
          </List>
        )}

        {/* Upload Progress */}
        {uploadProgress > 0 && uploadProgress < 100 && (
          <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 1 }}>
            <LinearProgress 
              variant="determinate" 
              value={uploadProgress} 
              sx={{ borderRadius: 1 }}
            />
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              Uploading... {uploadProgress}%
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Selected File Preview */}
      {file && (
        <Paper sx={{ 
          p: 1.5, 
          mx: 2, 
          mt: 1, 
          borderRadius: 2,
          bgcolor: 'primary.50',
          border: '1px solid',
          borderColor: 'primary.100',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {file.type.startsWith('audio/') ? (
                <Avatar sx={{ bgcolor: 'secondary.main', width: 40, height: 40 }}>
                  <Mic />
                </Avatar>
              ) : (
                <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                  {getFileIcon(file.name)}
                </Avatar>
              )}
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  {file.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(file.size)}
                  {isRecording && ` • Recording: ${formatRecordingTime(recordingTime)}`}
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={removeFile}>
              <Cancel />
            </IconButton>
          </Box>
        </Paper>
      )}

      {/* Message Input */}
      <Paper sx={{ 
        p: 2, 
        borderTop: '1px solid',
        borderColor: 'divider',
        position: 'relative',
      }}>
        {/* Simple Emoji Picker */}
        <AnimatePresence>
          {showEmojiPicker && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              style={{ 
                position: 'absolute', 
                bottom: '100%', 
                right: 0, 
                zIndex: 10,
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: '200px' }}>
                {['smile', 'heart', 'thumbsup', 'fire', 'check', 'warning', 'question', 'star', 'party', 'clap'].map((emoji) => (
                  <IconButton
                    key={emoji}
                    onClick={() => {
                      handleEmojiClick(emoji);
                      setShowEmojiPicker(false);
                    }}
                    size="small"
                  >
                    {emoji === 'smile' && '😊'}
                    {emoji === 'heart' && '❤️'}
                    {emoji === 'thumbsup' && '👍'}
                    {emoji === 'fire' && '🔥'}
                    {emoji === 'check' && '✅'}
                    {emoji === 'warning' && '⚠️'}
                    {emoji === 'question' && '❓'}
                    {emoji === 'star' && '⭐'}
                    {emoji === 'party' && '🎉'}
                    {emoji === 'clap' && '👏'}
                  </IconButton>
                ))}
              </Box>
            </motion.div>
          )}
        </AnimatePresence>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* File Upload */}
          <input
            ref={fileInputRef}
            accept="image/*,.pdf,.doc,.docx,audio/*"
            style={{ display: 'none' }}
            id="file-upload"
            type="file"
            onChange={handleFileUpload}
          />
          
          <Tooltip title="Attach file">
            <IconButton 
              component="span"
              htmlFor="file-upload"
              disabled={loading}
            >
              <AttachFile />
            </IconButton>
          </Tooltip>

          {/* Voice Recording */}
          <Tooltip title={isRecording ? "Stop recording" : "Voice message"}>
            <IconButton 
              onClick={isRecording ? stopRecording : startRecording}
              color={isRecording ? "error" : "default"}
              disabled={loading}
            >
              {isRecording ? <Stop /> : <KeyboardVoice />}
            </IconButton>
          </Tooltip>

          {/* Emoji Picker */}
          <Tooltip title="Emoji">
            <IconButton 
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              disabled={loading}
            >
              <EmojiEmotions />
            </IconButton>
          </Tooltip>

          {/* Message Input */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Type your message..."
            value={newMessage}
            onChange={(e) => {
              setNewMessage(e.target.value);
              handleTyping();
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            disabled={loading}
            multiline
            maxRows={4}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'white',
              },
            }}
          />

          {/* Send Button */}
          <motion.div
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <Tooltip title="Send message">
              <span>
                <IconButton
                  color="primary"
                  onClick={handleSendMessage}
                  disabled={loading || (!newMessage.trim() && !file)}
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'grey.300',
                      color: 'grey.500',
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    <Send />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </motion.div>
        </Box>

        {/* Quick Actions */}
        <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'center' }}>
          <Chip
            label="Quick questions"
            size="small"
            variant="outlined"
            onClick={() => setNewMessage("What are your shipping rates?")}
            disabled={loading}
          />
          <Chip
            label="Request quote"
            size="small"
            variant="outlined"
            onClick={() => setNewMessage("Can I get a quote for bulk order?")}
            disabled={loading}
          />
          <Chip
            label="Shipping info"
            size="small"
            variant="outlined"
            onClick={() => setNewMessage("What are the shipping timelines?")}
            disabled={loading}
          />
        </Box>
      </Paper>

      {/* Features Bar */}
      <Paper sx={{ 
        p: 1, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'grey.50',
      }}>
        <Typography variant="caption" color="text.secondary">
          AI-powered chat • End-to-end encrypted • File sharing up to 10MB
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Video call">
            <IconButton size="small">
              <Videocam fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="More options">
            <IconButton size="small">
              <MoreVert fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setShowSnackbar(false)} 
          severity="info"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes typing {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-5px); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </Box>
  );
};

export default ChatWindow;